"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Check, Plus, Info, X } from "lucide-react";
import { useEffect, useCallback, useMemo, useState, type FormEvent } from "react";
const BRAND_GREEN = "#436C1A";
const BRAND_YELLOW = "#FCC22F";
const PIZZA_OPTIONS = ["Marinara (Vegan)", "Margherita (Vegetarisch)", "Verdechiosa (Vegetarisch)", "Pera (Vegetarisch)", "Rossochiosa (Meat Lovers)", "Pistacchiosa (Meat Lovers)", "Forestella (Meat Lovers)", "Stella dolce (Sweet)"] as const;
type PizzaOption = (typeof PIZZA_OPTIONS)[number];
const PIZZA_IMAGES: Record<PizzaOption, string> = {
  "Marinara (Vegan)": "https://namo-catering.de/wp-content/uploads/2026/02/Marinara.remini-enhanced.jpg",
  "Margherita (Vegetarisch)": "https://namo-catering.de/wp-content/uploads/2026/02/Margherita.remini-enhanced.jpg",
  "Verdechiosa (Vegetarisch)": "https://namo-catering.de/wp-content/uploads/2026/02/Verdechiosa.remini-enhanced.jpg",
  "Pera (Vegetarisch)": "https://namo-catering.de/wp-content/uploads/2026/02/Pera.remini-enhanced.jpg",
  "Rossochiosa (Meat Lovers)": "https://namo-catering.de/wp-content/uploads/2026/02/Rossochiosa.remini-enhanced.jpg",
  "Pistacchiosa (Meat Lovers)": "https://namo-catering.de/wp-content/uploads/2026/02/Pistacchiosa.remini-enhanced.jpg",
  "Forestella (Meat Lovers)": "https://namo-catering.de/wp-content/uploads/2026/02/Forestella.remini-enhanced.jpg",
  "Stella dolce (Sweet)": "https://namo-catering.de/wp-content/uploads/2026/02/Stella-dolce.remini-enhanced.jpg"
};
const PIZZA_DESCRIPTIONS: Record<string, string> = {
  Marinara: "Aromatische Tomatensauce, feiner Knoblauch, frischer Oregano, frisches Basilikum und ein Hauch hochwertiges Olivenöl. Ein purer, perfekter Pizza-Klassiker.",
  Margherita: "Der Klassiker mit leckerer Tomatensauce, zart schmelzendem Mozzarella, frischem Basilikum und hochwertigem Olivenöl.",
  Verdechiosa: "Cremiges Pesto mit einem Hauch Zitrone, Mozzarella und sonnengereifte Tomaten. Abgerundet mit frischem Basilikum, Pecorino und feinem Olivenöl – Sommer auf dem Teller.",
  Pera: "Süße Birne trifft auf herzhaften Gorgonzola, zarten Mozzarella, feinen Honig, frischem Basilikum und hochwertiges Olivenöl – ein Genussmoment der besonderen Art.",
  Forestella: "Leckere, süßliche Tomatensauce trifft auf zart schmelzendem Mozzarella, verfeinert mit frischem Basilikum und hochwertiges Olivenöl. Getoppt mit hauchdünnem Prosciutto, knackigem Rucola und feinen Pecorino!",
  Rossochiosa: "Eine leidenschaftliche Komposition aus aromatischer Tomatensauce, zartschmelzendem Büffelmozzarella, würziger neapolitanischer Salami, süßer roter Spitzpaprika, frischem Basilikum, hochwertigem Olivenöl und einem feinen Hauch Chili-Honig. Scharf. Süß. Sinnlich.",
  Pistacchiosa: "Cremige Pistaziencreme trifft auf zart schmelzenden Mozzarella und feine Mortadella. Getoppt mit gehackten Pistazien, etwas Pecorino und einem Hauch Olivenöl. Vollendeter Genuss in Grün.",
  "Stella dolce": "Süße Dessertpizza mit cremigem Nutella und knackigem Haselnuss-Krokant – jede Sternspitze ist mit zartschmelzendem Nutella gefüllt."
};
export default function EventDetailsForm() {
  return;
}
function EventDetailsFormContent() {
  const {
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control,
    watch,
    setValue,
    setError
  } = useWvcForm();
  const [infoPizza, setInfoPizza] = useState<PizzaOption | null>(null);
  const eventType = watch("eventType");
  const isPrivateEvent = eventType === "Privates Event";
  const maxPizzas = useMemo(() => {
    if (!eventType) return 0;
    return eventType === "Berufliches Event" ? 5 : eventType === "Hochzeit" ? 6 : 4;
  }, [eventType]);
  const pizzasValue = watch("pizzas");
  const parsePizza = (pizza: PizzaOption) => {
    const hasMeta = pizza.includes(" (");
    const title = hasMeta ? pizza.split(" (")[0] : pizza;
    const meta = hasMeta ? pizza.split(" (")[1].replace(")", "") : "";
    return {
      title,
      meta
    };
  };
  useEffect(() => {
    if (!isPrivateEvent) setValue("privateEventSetup", "");
  }, [isPrivateEvent, setValue]);
  useEffect(() => {
    if (!eventType) {
      setValue("pizzas", []);
      return;
    }
    const current = Array.isArray(pizzasValue) ? pizzasValue : [];
    if (current.length > maxPizzas) setValue("pizzas", current.slice(0, maxPizzas));
  }, [eventType, maxPizzas, pizzasValue, setValue]);
  useEffect(() => {
    if (!infoPizza) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setInfoPizza(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [infoPizza]);
  const onSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await handleSubmit();
      const formData = watch();
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      if (!response.ok) {
        throw new Error("Fehler beim Senden der Anfrage. Bitte versuchen Sie es später erneut.");
      }
    } catch (error: any) {
      setError("root", {
        type: "manual",
        message: error.message || "Ein unerwarteter Fehler ist aufgetreten."
      });
    }
  }, [handleSubmit, watch, setError]);
  if (isSubmitted && !submitError) {
    return <div className="flex justify-center py-16">
        <div className="p-10 bg-white border border-[#436C1A]/20 rounded-2xl shadow-xl max-w-2xl text-center">
          <div className="w-16 h-16 bg-[#436C1A]/10 text-[#436C1A] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} strokeWidth={3} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Erfolgreich gesendet!</h3>
          <p className="text-gray-600 text-lg mb-8">{successMessage}</p>
          <Button onClick={resetForm} className="bg-[#436C1A] hover:bg-[#345414] text-white px-8 py-6 rounded-xl text-lg transition-colors">
            Weitere Anfrage senden
          </Button>
        </div>
      </div>;
  }
  const inputClass = "bg-white border border-slate-200 text-slate-900 rounded-xl shadow-sm transition-all duration-300 focus-visible:ring-2 focus-visible:ring-[#436C1A]/25 focus-visible:border-[#436C1A] hover:border-slate-300 placeholder:text-slate-400 px-4 py-4 leading-tight";
  const selectClass = "bg-white border border-slate-200 text-slate-900 rounded-xl shadow-sm transition-all duration-300 focus-visible:ring-2 focus-visible:ring-[#436C1A]/25 focus-visible:border-[#436C1A] hover:border-slate-300 w-full h-14 px-4 pr-12 py-3 leading-tight font-semibold appearance-none cursor-pointer";
  const infoData = infoPizza ? parsePizza(infoPizza) : null;
  const infoDescription = infoData ? PIZZA_DESCRIPTIONS[infoData.title] : "";
  const infoImage = infoPizza ? PIZZA_IMAGES[infoPizza] : "";
  return <>
      {infoPizza && infoData && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setInfoPizza(null)} />
          <div className="relative w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" role="dialog" aria-modal="true">
            <div className="h-2 w-full" style={{
          background: `linear-gradient(90deg, ${BRAND_GREEN}, ${BRAND_YELLOW})`
        }} />
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-3xl font-extrabold text-gray-900 mb-2">{infoData.title}</h3>
                  {infoData.meta && <span className="inline-flex items-center rounded-full bg-[#436C1A]/10 px-3 py-1 text-xs font-bold text-[#436C1A]">
                      {infoData.meta}
                    </span>}
                </div>

                <Button variant="ghost" size="icon" onClick={() => setInfoPizza(null)} className="shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors" aria-label="Schließen">
                  <X size={20} />
                </Button>
              </div>

              <div className="rounded-2xl overflow-hidden bg-slate-50 border border-slate-100 mb-6">
                <img src={infoImage} alt={infoData.title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
              </div>

              <p className="text-lg text-slate-600 leading-relaxed mb-8">{infoDescription || "Beschreibung folgt."}</p>

              <Button type="button" onClick={() => setInfoPizza(null)} className="w-full py-6 text-lg font-semibold rounded-xl" style={{
            backgroundColor: BRAND_GREEN,
            color: "white"
          }}>
                Schließen
              </Button>
            </div>
          </div>
        </div>}

      <div className="bg-white rounded-3xl p-7 sm:p-9 shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden textcenter">
        <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full blur-[80px] opacity-60" style={{
        background: `${BRAND_YELLOW}33`
      }} />
        <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full blur-[80px] opacity-50" style={{
        background: `${BRAND_GREEN}2B`
      }} />
        <div className="h-1.5 w-28 rounded-full mb-5 mx-auto" style={{
        background: `linear-gradient(90deg, ${BRAND_GREEN}, ${BRAND_YELLOW})`
      }} />

        <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
          Planen Sie Ihr <span style={{
          color: BRAND_GREEN
        }}>Event</span>
        </h2>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed font-medium max-w-3xl mx-auto">
          Ob private Feier, berufliche Veranstaltung oder Hochzeit – wir gestalten unvergessliche kulinarische Erlebnisse.
          Füllen Sie das Formular aus und wir erstellen ein maßgeschneidertes Angebot.
        </p>

        <div className="mt-7 h-2 rounded-full bg-slate-100 overflow-hidden max-w-3xl mx-auto">
          <div className="h-full w-2/5 rounded-full" style={{
          backgroundColor: BRAND_GREEN
        }} />
        </div>
      </div>

      <Form id="event-details-form-main" onSubmit={onSubmit}>
        
      </Form>
    </>;
}